

import { useState, useRef, useCallback } from "react";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";

interface ImageUploadProps {
  onFileSelect?: (file: File | null, compressedBlob?: Blob) => void;
  onUpload?: (file: File, preview: string) => void;
  selectedFile?: File | null;
  label?: string;
  description?: string;
  maxSize?: number;
  acceptedFormats?: string[];
  compressToWebP?: boolean;
  quality?: number;
  className?: string;
  showPreview?: boolean;
}

export function ImageUpload({
  onFileSelect,
  onUpload,
  selectedFile: externalSelectedFile,
  label = "Upload receipt/screenshot",
  description,
  maxSize = 2 * 1024 * 1024, // 2MB default
  acceptedFormats = ["image/jpeg", "image/png", "image/webp", "application/pdf"],
  compressToWebP = true,
  quality = 0.85,
  className = "",
  showPreview = true,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [internalPreview, setInternalPreview] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [internalSelectedFile, setInternalSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use external state if provided, otherwise use internal
  const selectedFile = externalSelectedFile !== undefined ? externalSelectedFile : internalSelectedFile;

  const compressImageToWebP = useCallback(
    async (file: File): Promise<{ blob: Blob; file: File }> => {
      return new Promise((resolve, reject) => {
        // Skip compression for PDFs
        if (file.type === "application/pdf") {
          resolve({ blob: file, file });
          return;
        }

        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Canvas context not available"));
          return;
        }

        img.onload = () => {
          // Calculate new dimensions (max 1920px width/height)
          let width = img.width;
          let height = img.height;
          const maxDimension = 1920;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to WebP
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to compress image"));
                return;
              }

              // Create new file with .webp extension
              const originalName = file.name.replace(/\.[^/.]+$/, "");
              const compressedFile = new File([blob], `${originalName}.webp`, {
                type: "image/webp",
                lastModified: Date.now(),
              });

              resolve({ blob, file: compressedFile });
            },
            "image/webp",
            quality
          );
        };

        img.onerror = () => reject(new Error("Failed to load image"));

        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
    },
    [quality]
  );

  const processFile = async (file: File) => {
    setError(null);
    setIsCompressing(true);

    try {
      // Check file size
      if (file.size > maxSize) {
        throw new Error(
          `File size exceeds ${(maxSize / 1024 / 1024).toFixed(1)}MB limit`
        );
      }

      // Check if format is accepted
      if (!acceptedFormats.includes(file.type)) {
        throw new Error(
          `Invalid file format. Accepted: ${acceptedFormats.join(", ")}`
        );
      }

      // Check if it's a PDF (keep as-is)
      const isPDF = file.type === "application/pdf";
      const isImage = file.type.startsWith("image/");

      let processedFile = file;
      let compressedBlob: Blob | undefined;

      if (isImage && compressToWebP) {
        const result = await compressImageToWebP(file);
        processedFile = result.file;
        compressedBlob = result.blob;
      }

      // Generate preview for images
      if (isImage && showPreview) {
        const previewReader = new FileReader();
        previewReader.onload = (e) => {
          const previewUrl = e.target?.result as string;
          setInternalPreview(previewUrl);
          if (onUpload) {
            onUpload(processedFile, previewUrl);
          }
        };
        previewReader.readAsDataURL(compressedBlob || processedFile);
      } else {
        // PDF or no preview
        setInternalPreview(null);
      }

      // Update internal state
      setInternalSelectedFile(processedFile);

      // Call external handler
      if (onFileSelect) {
        onFileSelect(processedFile, compressedBlob);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Upload failed";
      setError(errorMsg);
      console.error("File processing error:", err);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleClear = () => {
    if (internalPreview) {
      URL.revokeObjectURL(internalPreview);
    }
    setInternalPreview(null);
    setInternalSelectedFile(null);
    setError(null);
    if (onFileSelect) {
      onFileSelect(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const isPDF = selectedFile?.type === "application/pdf";
  const isImage = selectedFile?.type?.startsWith("image/");

  // Compact view for payment forms
  if (selectedFile) {
    return (
      <div className={className}>
        <label className="block text-[10px] font-medium text-stone-500 mb-1">
          Attached file
        </label>
        <div className="flex items-center gap-2 p-2 bg-stone-50 border border-stone-200 rounded">
          {/* Preview or Icon */}
          <div className="w-12 h-12 shrink-0 bg-white rounded border border-stone-200 flex items-center justify-center overflow-hidden">
            {isPDF ? (
              <FileText className="w-6 h-6 text-red-500" />
            ) : internalPreview ? (
              <img
                src={internalPreview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="w-6 h-6 text-stone-400" />
            )}
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-stone-700 truncate">{selectedFile.name}</p>
            <p className="text-[10px] text-stone-500">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              {isImage && compressToWebP && (
                <span className="text-emerald-600 ml-1">• Compressed</span>
              )}
            </p>
          </div>

          {/* Remove Button */}
          <button
            type="button"
            onClick={handleClear}
            className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Upload view
  return (
    <div className={className}>
      <label className="block text-[10px] font-medium text-stone-500 mb-1">
        {label} {compressToWebP && "• Auto WebP"}
      </label>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
          isDragging
            ? "border-amber-400 bg-amber-50"
            : "border-stone-300 bg-stone-50 hover:border-amber-300"
        } ${isCompressing ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(",")}
          onChange={handleFileSelect}
          disabled={isCompressing}
          className="hidden"
        />

        {isCompressing ? (
          <div className="space-y-2">
            <div className="flex justify-center">
              <div className="w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-sm font-medium text-stone-700">
              Compressing to WebP...
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <Upload className="w-6 h-6 mx-auto text-stone-400" />
            <p className="text-sm font-medium text-stone-700">
              Click or drag to upload
            </p>
            <p className="text-xs text-stone-500">
              {acceptedFormats.includes("application/pdf") ? "Photo or PDF" : "Photo"}
            </p>
            <p className="text-[10px] text-stone-400">
              Max {(maxSize / 1024 / 1024).toFixed(0)}MB
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 p-2 rounded bg-rose-50 text-rose-700 text-xs">
          {error}
        </div>
      )}

      {description && !error && (
        <p className="text-[9px] text-stone-400 mt-1">{description}</p>
      )}
    </div>
  );
}

export default ImageUpload;
